import { HTMLProps, useState } from 'react';

import { Link } from 'react-router';

import { IconChevronDown, IconMenu2, IconX } from '@tabler/icons-react';
import { useAtom } from 'jotai';
import { useTranslation } from 'react-i18next';

import {
  Drawer,
  IconButton,
  NavLink,
  TopNavigation,
  TopNavigationContent,
  TopNavigationContentShift,
  Typography,
} from '@cfa/react-components';

import { useAuth } from '@/hooks/useAuth';

import { cn } from '@/utils/tailwind';

import NavCountryLabel from './NavCountryLabel';

import { licenseeLocationAtom } from '@/atoms/licensee-location';
import { PathwayLogo } from '@/icons';

interface NavbarMobileProps {
  tabs: {
    icon: React.ReactNode;
    isActive: boolean;
    label: string;
    to: string;
  }[];
}

const NavbarMobile: React.FC<NavbarMobileProps & HTMLProps<HTMLDivElement>> = ({
  tabs,
  className,
}) => {
  const { t } = useTranslation();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { data } = useAuth();
  const isLicenseeUser = data?.isLicensee;
  const userLicenseeLocations = data?.licenseeLocations ?? [];

  const [{ selected: activeLicenseeLocation }, sendLicenseeLocationAction] =
    useAtom(licenseeLocationAtom);

  const showLicenseeLocation =
    isLicenseeUser &&
    !!activeLicenseeLocation?.number &&
    userLicenseeLocations.length > 1;

  const handleNavClick = () => {
    setDrawerOpen(false);
  };

  const handleLicenseeLocationClick = () => {
    sendLicenseeLocationAction('show');
  };

  return (
    // Intentionally using z-[1252] as it was lifted from wrapping TopNavigation CFA component.
    // ^ updated this to z-[1249] because it's sitting above modals.
    <div className={cn('sticky inset-0 z-1249', className)}>
      <TopNavigation
        className="h-header-height"
        style={{
          top: 'var(--web-alert-banner-height, 0px)',
        }}
      >
        <TopNavigationContent section="start">
          <Link data-testid="PathwayLogo" title={t('Generic.appName')} to={`/`}>
            <PathwayLogo className="h-8 w-7" />
          </Link>
          <NavCountryLabel isMobile />
          {showLicenseeLocation && (
            <button
              className="flex items-center text-left"
              onClick={handleLicenseeLocationClick}
              type="button"
            >
              <Typography
                className="pl-2"
                color="secondary"
                variant="overline3"
              >
                {activeLicenseeLocation.name}
              </Typography>

              <IconChevronDown className="text-brand-navy" />
            </button>
          )}
        </TopNavigationContent>

        {/* not rendering middle section */}
        <div style={{ flex: 1 }} />

        <TopNavigationContent section="end">
          <IconButton
            aria-label={`${drawerOpen ? 'close' : 'open'} menu`}
            className="collapse-menu"
            color="default"
            onClick={() => setDrawerOpen(prevState => !prevState)}
            size="sm"
          >
            {drawerOpen ? <IconX /> : <IconMenu2 />}
          </IconButton>
        </TopNavigationContent>
      </TopNavigation>
      <Drawer
        content={
          <>
            <TopNavigationContentShift />
            <div
              style={{
                marginTop: '16px',
                display: 'flex',
                flexDirection: 'column',
                padding: 4,
              }}
            >
              {tabs.map((tab, index) => (
                <NavLink
                  aria-label={`Navigate to ${tab.label}`}
                  as={Link}
                  data-testid={`Nav${tab.label}`}
                  icon={tab.icon}
                  isActive={tab.isActive}
                  key={index}
                  onClick={() => handleNavClick()}
                  to={tab.to}
                >
                  {tab.label}
                </NavLink>
              ))}
            </div>
          </>
        }
        onClose={() => setDrawerOpen(false)}
        open={drawerOpen}
      />
    </div>
  );
};

export default NavbarMobile;
